import {
  useClickAway,
  useLocalStorage,
  useLongPress,
} from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';

import tiles from '../../../config/tiles.config';
import UpdateHandlers from '../../../types/updateHandlers.type';
import TileType from '../types/tile.type';
import useTileActionHandler from './useTileActionHandler';

const getInitialTile = (tiles: TileType[]) =>
  tiles.filter((tile) => tile.action === 'add-tile');

type Args = {
  updateDialogStates: UpdateHandlers<unknown>;
  listName: string;
};

const useTile = ({ updateDialogStates, listName }: Args) => {
  const [tilesList, setTilesList] = useLocalStorage<TileType[]>(
    listName,
    getInitialTile(tiles),
  );

  const [tilesMode, setTilesMode] = useState<'idle' | 'edit'>('idle');

  const tileListRef = useClickAway(() => setTilesMode('idle'));

  const longPressAttrs = useLongPress(() => setTilesMode('edit'), {
    threshold: 1500,
  });

  const { tileActionHandlers } = useTileActionHandler({
    updateDialogStates,
  });

  const handleAddTile = (newTile: TileType) => {
    const tileIndex = tilesList.findIndex((tile) => tile.id === newTile.id);

    const newTilesList =
      tileIndex !== -1
        ? tilesList.map((tile, index) => (index === tileIndex ? newTile : tile))
        : [...tilesList, newTile];

    setTilesList(newTilesList);
  };

  const handleEditTile = (updatedTile: TileType) => {
    setTilesList((prev) =>
      prev.map((tile) => (tile.id === updatedTile.id ? updatedTile : tile)),
    );
  };

  const handleRemoveTile = (tileId: string) => {
    const updatedTilesList = tilesList.filter((tile) => tile.id !== tileId);
    setTilesList(updatedTilesList);
    setTilesMode(updatedTilesList.length === 0 ? 'idle' : 'edit');
  };

  useEffect(() => {
    if (tilesList.length === 1) {
      setTilesMode('idle');
    }
  }, [tilesList.length]);

  return {
    tiles,
    tileActionHandlers,
    tilesList,
    tilesMode,
    tileListRef,
    longPressAttrs,
    setTilesList,
    handleAddTile,
    handleEditTile,
    handleRemoveTile,
    setTilesMode,
  };
};

export default useTile;
